package dev.lingvo.shared.screenshots.text

import dev.lingvo.shared.TextDirection
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.FitSpec
import dev.lingvo.shared.screenshots.doc.TextAlign
import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.doc.VAlign
import dev.lingvo.shared.screenshots.render.CanvasAlign
import dev.lingvo.shared.screenshots.render.TextMeasurer
import kotlin.math.round

/** Font coverage follows writing systems rather than a device's installed fonts. */
enum class Script {
    LATIN, VIETNAMESE, CYRILLIC, GREEK, ARABIC, URDU, HEBREW, DEVANAGARI, BENGALI, GUJARATI,
    GURMUKHI, KANNADA, MALAYALAM, TAMIL, TELUGU, THAI, KHMER, MYANMAR, HANS, HANT, HK, JAPANESE, KOREAN,
}

/** Explicit families precede one CSS generic fallback so measuring and drawing agree. */
data class FontStack(val families: List<String>, val generic: String)

/** Separates mandatory breaks from opportunities a fitter may choose. */
enum class Brk { NONE, SPACE, SOFT_HYPHEN, ALLOWED, HARD }

/** Keeps a break attached to the text before it, including discretionary hyphens. */
data class Segment(val text: String, val breakAfter: Brk)

/** Locale-aware segmentation is injected so JVM and browser fitting use the same rules. */
interface Segmenter {
    /** Retains the input's text while identifying legal line endings. */
    fun segments(text: String, locale: String): List<Segment>
}

/** All fitting inputs are explicit so an export cannot depend on editor state. */
data class FitRequest(
    val text: String, val locale: String, val boxW: Double, val boxH: Double, val font: FontStack,
    val weight: Int, val italic: Boolean, val spec: FitSpec, val lineHeight: Double, val letterSpacingEm: Double,
    val align: TextAlign, val vAlign: VAlign, val uppercase: Boolean = false, val quoted: Boolean = false,
    val step: Double = 0.5,
)

/** Stable refusal reasons let the editor explain how to repair an export. */
enum class Refusal { WORD_TOO_WIDE, TOO_MANY_LINES, TOO_TALL, NO_BREAK_OPPORTUNITY, BOX_TOO_SMALL, FONT_NOT_LOADED }

/** Box-local anchors preserve exactly what the measurer approved. */
data class LaidLine(
    val text: String, val width: Double, val anchorX: Double, val baselineY: Double,
    val canvasAlign: CanvasAlign, val hyphenated: Boolean,
)

/** Layout metrics are shared by the renderer, fit badges and editor overlays. */
data class TextLayout(
    val size: Double, val lines: List<LaidLine>, val blockHeight: Double, val direction: TextDirection,
    val lineAdvance: Double, val letterSpacingPx: Double,
)

/** A refusal retains the floor layout so editors can show the actual overflow. */
sealed interface FitResult {
    /** Preserves the measured geometry for both accepted and refused text. */
    val layout: TextLayout
    /** Records successful fitting decisions for review. */
    data class Fitted(
        override val layout: TextLayout, val shrunk: Boolean, val usedHyphen: Boolean, val balanced: Boolean,
    ) : FitResult
    /** Carries actionable overflow measurements at the font floor. */
    data class Refused(
        override val layout: TextLayout, val reason: Refusal, val overflowX: Double,
        val overflowY: Double, val extraLines: Int,
    ) : FitResult
    /** Empty captions still have deterministic geometry and direction. */
    data class Empty(override val layout: TextLayout) : FitResult
}

/** Injects fitting without tying the renderer to a platform font API. */
fun interface CaptionFitter {
    /** Fits with the same measurement and break rules used for drawing. */
    fun fit(req: FitRequest, measurer: TextMeasurer, segmenter: Segmenter): FitResult
}

/** Locale and sample text jointly choose a supported writing system. */
fun interface ScriptResolver {
    /** Uses the sample when a locale alone cannot identify the script. */
    fun scriptFor(locale: String, sample: String?): Script
}

/** Resolves brand and template choices before platform font loading. */
fun interface FontResolver {
    /** Returns only families approved for this role and script. */
    fun stack(role: TypeRole, explicit: String?, brand: BrandKit, templateFamily: String?, script: Script, emoji: Boolean): FontStack
}

/** Quotes every family, refusing CSS syntax so untrusted font names cannot inject declarations. */
fun cssFont(stack: FontStack, sizePx: Double, weight: Int, italic: Boolean): String {
    require(stack.generic in setOf("sans-serif", "serif", "monospace")) { "Unsupported generic font family" }
    require(sizePx.isFinite() && sizePx > 0) { "Font size must be positive and finite" }
    require(weight in 1..1000) { "Font weight must be between 1 and 1000" }
    require(stack.families.isNotEmpty()) { "At least one font family is required" }
    stack.families.forEach { family ->
        require(family.isNotBlank() && family.none { it in "\"\\;{}" || it.code < 32 || it.code in 127..159 }) {
            "Font family contains CSS syntax or control characters"
        }
    }
    val size = fontSize(sizePx)
    val families = stack.families.joinToString(", ") { "\"$it\"" }
    return "${if (italic) "italic " else ""}$weight ${size}px $families, ${stack.generic}"
}

private fun fontSize(sizePx: Double): String {
    val scaled = sizePx * 100
    val rounded = if (scaled.isFinite()) round(scaled) / 100 else sizePx
    val parts = rounded.toString().lowercase().split('e')
    val mantissa = parts[0]
    val exponent = parts.getOrNull(1)?.toInt() ?: 0
    val digits = mantissa.replace(".", "")
    val point = (mantissa.indexOf('.').takeIf { it >= 0 } ?: mantissa.length) + exponent
    val whole = if (point <= 0) "0" else digits.take(point).padEnd(point, '0')
    val fraction = if (point < 0) "0".repeat(-point) + digits else digits.drop(point)
    return "$whole.${fraction.padEnd(2, '0').take(2)}"
}
